import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Link,
  Stack,
  Switch,
  Typography,
  styled,
} from "~/common/mui";
import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import { invalidateSession } from "~/app/api/pageData";
import { useSessionUser } from "~/app/config/useSession";

type DesignToggleContextType = {
  isNewDesignEnabled: boolean;
  isLoading: boolean;
  toggleDesign: () => Promise<void>;
};

const DesignToggleContext = createContext<DesignToggleContextType | null>(null);

export const useDesignToggle = () => {
  const context = useContext(DesignToggleContext);

  if (!context) {
    throw new Error("useDesignToggle must be used inside OrderListDesignToggleProvider");
  }

  return context;
};

type ProviderProps = {
  children: ReactNode;
};

const updateDesignPreference = async (useNewDesign: boolean) => {
  const params = new URLSearchParams();
  params.set("orderUIPreference", String(useNewDesign));

  const url = occUrl(`/preferences/order-view-ui?${params}`);

  return occFetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const OrderListDesignToggleProvider = ({ children }: ProviderProps) => {
  const sessionUser = useSessionUser();
  const [isNewDesignEnabled, setIsNewDesignEnabled] = useState(sessionUser?.preferences?.newOrderView ?? true);

  useEffect(() => {
    if (sessionUser?.preferences?.newOrderView !== undefined) {
      setIsNewDesignEnabled(sessionUser.preferences.newOrderView);
    }
  }, [sessionUser?.preferences?.newOrderView]);

  const [isLoading, setIsLoading] = useState(false);

  const toggleDesign = useCallback(async () => {
    try {
      setIsLoading(true);

      const nextValue = !isNewDesignEnabled;

      const response = await updateDesignPreference(nextValue);
      if (!response.ok) {
        throw new Error(`Preference update failed: ${response.status}`);
      }

      setIsNewDesignEnabled(nextValue);
      invalidateSession();
    } catch (error) {
      console.error("Failed to update design preference", error);
    } finally {
      setIsLoading(false);
    }
  }, [isNewDesignEnabled]);

  const value = useMemo(
    () => ({
      isNewDesignEnabled,
      isLoading,
      toggleDesign,
    }),
    [isNewDesignEnabled, isLoading, toggleDesign],
  );

  return <DesignToggleContext.Provider value={value}>{children}</DesignToggleContext.Provider>;
};

const IFRAME_URL = "https://schaefflertech.qualtrics.com/jfe/form/SV_esa1k0NANhT4wPY";

export const OrderListDesignToggle = () => {
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState(false);
  const { isNewDesignEnabled, toggleDesign, isLoading } = useDesignToggle();

  const closeModal = () => setShowModal(false);

  return (
    <>
      <ToggleWrapper variant="elevation" elevation={1}>
        <Stack direction="row" gap={0.5}>
          <Typography color="text.primary">{t("quoteList.newDesign.description")}</Typography>
          <Link component={"button"} onClick={() => setShowModal(true)}>
            {t("feature.search.newDesign.feedback")}
          </Link>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1}>
          <Switch checked={isNewDesignEnabled} onChange={toggleDesign} disabled={isLoading} />
          <Typography color={isNewDesignEnabled ? "primary" : "text.primary"}>
            {t(`feature.search.newDesign.toggle.${isNewDesignEnabled ? "new" : "old"}`)}
          </Typography>
        </Stack>
      </ToggleWrapper>
      <Dialog open={showModal} onClose={closeModal} maxWidth="desktop-s">
        <DialogHeader onClose={closeModal}>
          <DialogTitle>{t("quoteList.newDesign.dialogTitle")}</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <iframe style={{ width: "100%", height: "600px", border: "none" }} src={IFRAME_URL} />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setShowModal(false)}>
            {t("cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ToggleWrapper = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  padding: "8px 12px",
  background: theme.palette.surface.main,
  justifyContent: "space-between",
  alignItems: "center",
}));
